import React from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { FileUploadOutlined } from "@mui/icons-material";
import Dialog from "../../../components/Dialog/Dialog";
import ImageCrop from "../../../components/ImageCrop/ImageCrop";

interface InsuranceImageInputProps {
  action: (image: Blob) => void;
  initialImage: string;
  isUploading?: boolean;
  onRemove?: () => void;
}

function InsuranceImageInput({
  action,
  initialImage,
  isUploading = false,
  onRemove
}: InsuranceImageInputProps): React.ReactElement {
  const imageRef = React.useRef<HTMLInputElement>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [cropperOpen, setCropperOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [temporaryImage, setTemporaryImage] = React.useState<string | null>(initialImage || null);

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size >= 2097152) {
      setDialogOpen(true);
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif"].includes(ext || "")) {
      setTemporaryImage(URL.createObjectURL(file));
      setCropperOpen(true);
    } else {
      setDialogOpen(true);
    }

    // clear input value so same file can be selected again
    const { target } = e;
    if (target) {
      target.value = "";
    }
  }

  const handleDelete = () => {
    onRemove?.();
    setTemporaryImage(null);
    setDeleteDialogOpen(false);
  };

  return (
    <Box display="flex" width="100%">
      {/* Delete confirmation dialog */}
      {deleteDialogOpen && (
        <Dialog
          title="Delete Insurance Image"
          description="Are you sure you want to delete this insurance image?"
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(false)}
          readMode={false}
        />
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          alignItems: "center"
        }}
      >
        {temporaryImage ? (
          <Box width="100%" display="flex" flexDirection="column" alignItems="center" gap="10px">
            <Box
              sx={{
                height: "250px",
                width: "100%",
                borderRadius: "5px",
                border: "2px dashed #b2beb5",
                textAlign: "center",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden"
              }}
            >
              <img
                src={temporaryImage}
                alt="Insurance Card"
                style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
              />
            </Box>
            <Button
              type="button"
              disabled={isUploading}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                width: "100%",
                textAlign: "center",
                borderRadius: "5px",
                padding: "8px",
                color: "#fff",
                backgroundColor: "red",
                "&:hover": { backgroundColor: "#c62828" }
              }}
            >
              Remove Insurance Image
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: "10px" }}>
            <Box
              sx={{
                border: "2px dashed #b2beb5",
                height: "250px",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "5px",
                cursor: "pointer"
              }}
              onClick={() => imageRef?.current?.click()}
            >
              {isUploading ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <FileUploadOutlined sx={{ fontSize: "50px", color: "#b2beb5" }} />
                  <p style={{ color: "#b2beb5", margin: 0 }}>Upload Insurance Image</p>
                  <p style={{ color: "#b2beb5", fontSize: "12px", margin: "4px 0 0" }}>
                    JPG, JPEG, PNG, GIF · Max 2MB
                  </p>
                </>
              )}
            </Box>

            <Button
              disabled={isUploading}
              variant="contained"
              sx={{ width: "100%" }}
              onClick={() => imageRef?.current?.click()}
            >
              {isUploading ? "Uploading..." : "Upload Insurance Image"}
            </Button>
          </Box>
        )}
      </Box>

      {/* Image cropper */}
      <ImageCrop
        image={temporaryImage}
        isOpen={cropperOpen}
        dynamicRatio={false}
        onCancel={() => {
          setCropperOpen(false);
          setTemporaryImage(initialImage || null);
        }}
        onSave={(img) => {
          action(img);
          setCropperOpen(false);
          setTemporaryImage(URL.createObjectURL(img));
        }}
      />

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/jpg,image/jpeg,image/png,image/gif"
        ref={imageRef}
        style={{ display: "none" }}
        onChange={handleImageChange}
        disabled={isUploading}
      />

      {/* Format/size error dialog */}
      {dialogOpen && (
        <Dialog
          title="Cannot upload image"
          description="File size limit is 2MB. Accepted formats: JPG, JPEG, PNG, GIF."
          next={() => setDialogOpen(false)}
          readMode
        />
      )}
    </Box>
  );
}

export default InsuranceImageInput;
